import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import PacientesSemNutricionistaPage from '@/pages/admin/PacientesSemNutricionistaPage';
import { ReverEstadosTab } from '@/components/admin/FollowupLists';
import { Users, MagnifyingGlass, Plus, Phone, Envelope, Clock, Check, X } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import CountryCodeSelect from '@/components/CountryCodeSelect';

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { ativo: 'bg-brand-mint/20 text-brand-green', pendente: 'bg-amber-50 text-amber-600', inativo: 'bg-red-50 text-red-500', arquivado: 'bg-gray-100 text-gray-400' };

export default function PacientesPage() {
  useRequireAdmin();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', country_code: '+351', client_id: '', birth_date: '', patient_type: 'geral', program: '', notes: '' });
  const [filter, setFilter] = useState('todos');
  const [typeFilter, setTypeFilter] = useState('');
  const [patientTags, setPatientTags] = useState([]);
  const [showEditTags, setShowEditTags] = useState(false);
  const [editTags, setEditTags] = useState([]);
  const [newTagVal, setNewTagVal] = useState('');
  const [newTagLabel, setNewTagLabel] = useState('');
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const semNutri = authUser?.role === 'admin' && searchParams.get('filtro') === 'sem-nutricionista';
  const reverEstados = authUser?.role === 'admin' && searchParams.get('filtro') === 'rever-estados';
  const setSemNutri = (on) => { const n = new URLSearchParams(searchParams); if (on) n.set('filtro', 'sem-nutricionista'); else n.delete('filtro'); setSearchParams(n); };

  const fetchPatients = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patients?search=${search}`); setPatients(data); } catch {} finally { setLoading(false); }
  }, [search]);
  const fetchTags = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/patient-tags`); setPatientTags(data.tags || []); } catch {}
  }, []);
  useEffect(() => { fetchPatients(); }, [fetchPatients]);
  useEffect(() => { fetchTags(); }, [fetchTags]);

  const saveTags = async () => {
    try { await axios.put(`${API}/api/patient-tags`, { tags: editTags }); setPatientTags(editTags); toast.success('Tags guardadas'); setShowEditTags(false); } catch { toast.error('Erro'); }
  };
  const getTagLabel = (val) => { const t = patientTags.find(t => t.value === val); return t ? t.label : val; };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/patients/create`, form);
      toast.success('Paciente criado com sucesso');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', phone: '', birth_date: '', patient_type: 'geral', program: '', notes: '' });
      fetchPatients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao criar paciente'); }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/api/patients/${userId}/status`, { status: newStatus });
      toast.success(`Status atualizado para ${newStatus}`);
      fetchPatients();
    } catch (err) { toast.error('Erro ao atualizar status'); }
  };

  const filtered = patients.filter(p => {
    const st = p.status || 'ativo';
    if (filter === 'todos') { if (st === 'arquivado') return false; }
    else if (filter !== st) return false;
    if (typeFilter) {
      const types = p.patient_types && p.patient_types.length ? p.patient_types : [p.patient_type || 'geral'];
      if (!types.includes(typeFilter)) return false;
    }
    return true;
  });
  const pendingCount = patients.filter(p => p.status === 'pendente').length;
  const archivedCount = patients.filter(p => p.status === 'arquivado').length;

  if (reverEstados) return (
    <div className="fade-in" data-testid="pacientes-page">
      <button onClick={() => setSearchParams({})} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-brand-border text-brand-muted mb-4" data-testid="filter-todas-pacientes">Todas as pacientes</button>
      <ReverEstadosTab />
    </div>
  );

  if (semNutri) return (
    <div className="fade-in" data-testid="pacientes-page">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setSemNutri(false)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-brand-border text-brand-muted" data-testid="filter-todas-pacientes">Todas as pacientes</button>
        <button className="px-3 py-1.5 rounded-full text-xs font-medium bg-brand-green text-white" data-testid="filter-sem-nutricionista">Sem nutricionista</button>
      </div>
      <PacientesSemNutricionistaPage />
    </div>
  );

  return (
    <div className="fade-in" data-testid="pacientes-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text" data-testid="pacientes-title">Pacientes</h2>
          <p className="text-sm text-brand-muted mt-1">{patients.length} pacientes{pendingCount > 0 && ` (${pendingCount} pendentes)`}</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="bg-brand-green text-white rounded-full px-4 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="create-patient-btn">
          <Plus size={16} /> Novo paciente
        </button>
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 mb-2 flex-wrap">
        {['todos', 'ativo', 'pendente', 'inativo', 'arquivado'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${filter === f ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`filter-${f}`}>
            {f === 'todos' ? 'Ativas' : f.charAt(0).toUpperCase() + f.slice(1)}{f === 'pendente' && pendingCount > 0 ? ` (${pendingCount})` : ''}{f === 'arquivado' && archivedCount > 0 ? ` (${archivedCount})` : ''}
          </button>
        ))}
        {authUser?.role === 'admin' && (
          <button onClick={() => setSearchParams({ filtro: 'rever-estados' })} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-brand-border text-brand-muted" data-testid="filter-rever-estados">Rever estados</button>
        )}
        {authUser?.role === 'admin' && (
          <button onClick={() => setSemNutri(true)} className="px-3 py-1.5 rounded-full text-xs font-medium bg-white border border-brand-border text-brand-muted" data-testid="filter-sem-nutricionista">Sem nutricionista</button>
        )}
      </div>
      {/* Type Filters */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        <button onClick={() => setTypeFilter('')} className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${!typeFilter ? 'bg-brand-gold text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>Todos os tipos</button>
        {patientTags.map(t => (
          <button key={t.value} onClick={() => setTypeFilter(typeFilter === t.value ? '' : t.value)} className={`px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${typeFilter === t.value ? 'bg-brand-gold text-white' : 'bg-white border border-brand-border text-brand-muted'}`} data-testid={`type-filter-${t.value}`}>{t.label}</button>
        ))}
        <button onClick={() => { setEditTags([...patientTags]); setShowEditTags(true); }} className="px-2 py-1 text-[10px] text-brand-green hover:underline whitespace-nowrap">Editar tags</button>
      </div>

      <div className="relative mb-4">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-placeholder" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Procurar por nome ou email..." className="pl-10 rounded-2xl border-brand-border" data-testid="patient-search" />
      </div>

      {loading ? <div className="text-center py-12 text-brand-muted">A carregar...</div> : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Users size={48} className="mx-auto text-brand-placeholder mb-4" weight="duotone" />
          <p className="text-brand-muted">{search ? 'Sem resultados.' : 'Sem pacientes.'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => {
            const status = p.status || 'ativo';
            const isPending = status === 'pendente';
            return (
              <div key={p.user_id} className={`bg-white border border-brand-border rounded-2xl p-4 flex items-center gap-4 ${status === 'arquivado' ? 'opacity-50 bg-gray-50' : ''}`}>
                <Link to={`/admin/pacientes/${p.user_id}`} className="flex items-center gap-4 flex-1 text-left min-w-0 no-underline text-inherit" data-testid={`patient-card-${p.user_id}`}>
                  <div className="w-10 h-10 rounded-full bg-brand-mint/20 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-medium text-brand-green text-sm">{(p.name || '?')[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-brand-text truncate">{p.name || 'Sem nome'}</p>
                    <span className="text-xs text-brand-muted flex items-center gap-1"><Envelope size={12} /> {p.email}</span>
                  </div>
                </Link>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {p.patient_type && p.patient_type !== 'geral' && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-brand-bg text-brand-muted">{getTagLabel(p.patient_type)}</span>
                  )}
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[status] || STATUS_COLORS.ativo}`}>{status}</span>
                  {p.program_badge === 'terminado' && <span className="text-[9px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full ml-1">Terminado</span>}
                  {p.program_badge?.startsWith('termina_em') && <span className="text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full ml-1">Termina em {p.program_days_left}d</span>}
                  {p.last_consultation_date && (() => {
                    const days = Math.floor((new Date() - new Date(p.last_consultation_date)) / 86400000);
                    return days > 30 ? <span className="text-[9px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded-full ml-1">Última consulta: {days}d</span> : null;
                  })()}
                  {isPending && (
                    <div className="flex gap-1">
                      <button onClick={() => handleStatusChange(p.user_id, 'ativo')} className="w-7 h-7 rounded-full bg-brand-mint/20 text-brand-green flex items-center justify-center hover:bg-brand-mint/40 transition-colors" title="Aprovar" data-testid={`approve-${p.user_id}`}><Check size={14} /></button>
                      <button onClick={() => handleStatusChange(p.user_id, 'inativo')} className="w-7 h-7 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors" title="Rejeitar" data-testid={`reject-${p.user_id}`}><X size={14} /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create patient dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Novo Paciente</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Nome *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl border-brand-border" required data-testid="new-patient-name" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl border-brand-border" required data-testid="new-patient-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Password (opcional)</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Deixar vazio se Google auth" className="rounded-xl border-brand-border" data-testid="new-patient-password" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Telefone</Label>
                <div className="flex gap-1">
                  <CountryCodeSelect value={form.country_code || '+351'} onChange={v => setForm({...form, country_code: v})} testId="new-patient-country-code" />
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="flex-1 rounded-xl border-brand-border" placeholder="912345678" data-testid="new-patient-phone" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-brand-muted mb-1 block">Data nascimento</Label><Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} className="rounded-xl border-brand-border" data-testid="new-patient-birth" /></div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
                <select value={form.patient_type} onChange={e => setForm({ ...form, patient_type: e.target.value })} className="w-full border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="new-patient-type">
                  {patientTags.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Programa</Label><Input value={form.program} onChange={e => setForm({ ...form, program: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <button type="submit" className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="submit-create-patient">Criar paciente</button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit tags dialog */}
      <Dialog open={showEditTags} onOpenChange={setShowEditTags}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Gerir tipos de paciente</DialogTitle></DialogHeader>
          <div className="space-y-2 mb-3">
            {editTags.map((t, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={t.value} onChange={e => { const u = [...editTags]; u[i] = { ...u[i], value: e.target.value }; setEditTags(u); }} placeholder="Chave (ex: gravida)" className="w-28 rounded-lg border-brand-border text-xs h-8" />
                <Input value={t.label} onChange={e => { const u = [...editTags]; u[i] = { ...u[i], label: e.target.value }; setEditTags(u); }} placeholder="Nome (ex: Grávida)" className="flex-1 rounded-lg border-brand-border text-xs h-8" />
                <button onClick={() => setEditTags(editTags.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X size={14} /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-center mb-3">
            <Input value={newTagVal} onChange={e => setNewTagVal(e.target.value)} placeholder="Chave" className="w-28 rounded-lg border-brand-border text-xs h-8" />
            <Input value={newTagLabel} onChange={e => setNewTagLabel(e.target.value)} placeholder="Nome visível" className="flex-1 rounded-lg border-brand-border text-xs h-8" onKeyDown={e => { if (e.key === 'Enter' && newTagVal.trim() && newTagLabel.trim()) { setEditTags([...editTags, { value: newTagVal.trim().toLowerCase().replace(/\s/g, '_'), label: newTagLabel.trim() }]); setNewTagVal(''); setNewTagLabel(''); }}} />
            <button onClick={() => { if (newTagVal.trim() && newTagLabel.trim()) { setEditTags([...editTags, { value: newTagVal.trim().toLowerCase().replace(/\s/g, '_'), label: newTagLabel.trim() }]); setNewTagVal(''); setNewTagLabel(''); }}} className="text-xs text-brand-green font-medium"><Plus size={14} /></button>
          </div>
          <button onClick={saveTags} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium">Guardar</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
